import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, CreditCard, Clock, Check, AlertTriangle, Upload, X, MessageCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SubscriptionSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [plans, setPlans] = useState([]);
  const [qrCode, setQrCode] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [showRenewModal, setShowRenewModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [step, setStep] = useState('plans'); // plans, qr, form
  const [formData, setFormData] = useState({ amount: '', utr_number: '', attachment: null });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [supportWhatsApp, setSupportWhatsApp] = useState('918248075883');

  useEffect(() => {
    fetchData();
    fetchSupportSettings();
  }, []);

  const fetchSupportSettings = async () => {
    try {
      const response = await axios.get(`${API}/public/settings`);
      if (response.data?.support_whatsapp) {
        setSupportWhatsApp(response.data.support_whatsapp.replace(/[^0-9]/g, ''));
      }
    } catch (err) {
    }
  };

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const [statusRes, plansRes, qrRes, historyRes] = await Promise.all([
        axios.get(`${API}/pos/subscription-status`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/public/plans`),
        axios.get(`${API}/public/payment-qr`),
        axios.get(`${API}/pos/my-payment-requests`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setSubscriptionStatus(statusRes.data);
      setPlans(plansRes.data);
      setQrCode(qrRes.data.qr_code);
      setPaymentHistory(historyRes.data);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData({ ...formData, attachment: reader.result });
      setError('');
    };
    reader.readAsDataURL(file);
  };

  const handleSubmitPayment = async () => {
    if (!formData.amount || !formData.utr_number) {
      setError('Please fill in amount and UTR number');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const token = localStorage.getItem('pos_token');
      await axios.post(`${API}/pos/submit-payment`, {
        plan_id: selectedPlan.id,
        amount: parseFloat(formData.amount),
        utr_number: formData.utr_number,
        attachment_base64: formData.attachment
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccess('Payment submitted successfully! Awaiting approval.');
      setShowRenewModal(false);
      setStep('plans');
      setSelectedPlan(null);
      setFormData({ amount: '', utr_number: '', attachment: null });
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit payment');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-700';
      case 'expired': return 'bg-red-100 text-red-700';
      case 'pending_approval': return 'bg-amber-100 text-amber-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-violet-200 border-t-violet-600"></div>
      </div>
    );
  }

  return (
    <div data-testid="subscription-settings-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/gposp/settings')}
          className="p-2 hover:bg-slate-100 rounded-xl transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Subscription</h1>
          <p className="text-slate-500">Manage your subscription plan</p>
        </div>
      </div>

      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-xl text-green-700 flex items-center gap-2">
          <Check size={20} />
          {success}
          <button onClick={() => setSuccess('')} className="ml-auto"><X size={18} /></button>
        </div>
      )}

      {/* Current Status Card */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-6 border-b border-slate-200 bg-gradient-to-r from-violet-50 to-purple-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center shadow-sm">
                <CreditCard className="text-violet-600" size={28} />
              </div>
              <div>
                <p className="text-sm text-slate-500">Current Plan</p>
                <p className="text-xl font-bold text-slate-800">
                  {subscriptionStatus?.plan_name || 'No Active Plan'}
                </p>
              </div>
            </div>
            <span className={`px-4 py-2 rounded-full text-sm font-medium ${getStatusColor(subscriptionStatus?.status)}`}>
              {subscriptionStatus?.status === 'active' ? 'Active' : 
               subscriptionStatus?.status === 'expired' ? 'Expired' :
               subscriptionStatus?.status === 'pending_approval' ? 'Pending Approval' : 'No Plan'}
            </span>
          </div>
        </div>
        
        <div className="p-6">
          {subscriptionStatus?.status === 'active' && (
            <div className="flex items-center gap-2 text-slate-600">
              <Clock size={18} />
              <span>Valid until: <strong>{formatDate(subscriptionStatus.valid_until)}</strong></span>
            </div>
          )}
          
          {subscriptionStatus?.status === 'expired' && (
            <div className="flex items-center gap-2 text-red-600">
              <AlertTriangle size={18} />
              <span>Expired on: <strong>{formatDate(subscriptionStatus.expired_at)}</strong></span>
            </div>
          )}
          
          {subscriptionStatus?.status === 'pending_approval' && (
            <div className="flex items-center gap-2 text-amber-600">
              <Clock size={18} />
              <span>Payment submitted, waiting for approval</span>
            </div>
          )}
          
          <div className="mt-4 flex gap-3">
            {(subscriptionStatus?.status === 'active' || subscriptionStatus?.status === 'expired' || subscriptionStatus?.status === 'no_subscription') && (
              <button
                onClick={() => setShowRenewModal(true)}
                className="px-6 py-2.5 bg-violet-600 text-white rounded-xl font-medium hover:bg-violet-700 transition-colors"
                data-testid="renew-btn"
              >
                {subscriptionStatus?.status === 'active' ? 'Extend Plan' : 'Subscribe Now'}
              </button>
            )}
            <a
              href={`https://wa.me/${supportWhatsApp}`}
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-2.5 border border-green-500 text-green-600 rounded-xl font-medium hover:bg-green-50 transition-colors flex items-center gap-2"
            >
              <MessageCircle size={18} />
              Need Help?
            </a>
          </div>
        </div>
      </div>

      {/* Payment History */}
      {paymentHistory.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-200">
            <h2 className="font-semibold text-slate-800">Payment History</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600">Plan</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600">Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600">UTR</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {paymentHistory.map((payment) => (
                  <tr key={payment.id}>
                    <td className="px-4 py-3 text-sm">{formatDate(payment.created_at)}</td>
                    <td className="px-4 py-3 text-sm font-medium">{payment.plan_name}</td>
                    <td className="px-4 py-3 text-sm">₹{payment.amount}</td>
                    <td className="px-4 py-3 text-xs font-mono">{payment.utr_number}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        payment.status === 'approved' ? 'bg-green-100 text-green-700' :
                        payment.status === 'rejected' ? 'bg-red-100 text-red-700' :
                        'bg-amber-100 text-amber-700'
                      }`}>
                        {payment.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Renew/Subscribe Modal */}
      {showRenewModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
              <h3 className="text-lg font-semibold">
                {step === 'plans' ? 'Select Plan' : step === 'qr' ? 'Scan & Pay' : 'Submit Payment'}
              </h3>
              <button onClick={() => { setShowRenewModal(false); setStep('plans'); setSelectedPlan(null); }} className="p-2 hover:bg-slate-100 rounded-lg">
                <X size={20} />
              </button>
            </div>
            
            <div className="p-4">
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
                  <AlertTriangle size={16} />
                  {error}
                </div>
              )}
              
              {/* Step 1: Plan Selection */}
              {step === 'plans' && (
                <div className="space-y-3">
                  {plans.map((plan) => (
                    <div
                      key={plan.id}
                      onClick={() => setSelectedPlan(plan)}
                      className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                        selectedPlan?.id === plan.id ? 'border-violet-600 bg-violet-50' : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="font-semibold text-slate-800">{plan.name}</p>
                          <p className="text-sm text-slate-500">{plan.features?.[0]}</p>
                        </div>
                        <p className="text-xl font-bold text-violet-600">₹{plan.price_inr}</p>
                      </div>
                    </div>
                  ))}
                  
                  <button
                    onClick={() => selectedPlan && setStep('qr')}
                    disabled={!selectedPlan}
                    className="w-full py-3 bg-violet-600 text-white rounded-xl font-medium hover:bg-violet-700 disabled:opacity-50 mt-4"
                  >
                    Continue to Payment
                  </button>
                </div>
              )}
              
              {/* Step 2: QR Code */}
              {step === 'qr' && (
                <div className="text-center space-y-4">
                  <div className="bg-violet-50 rounded-xl p-4">
                    <p className="text-sm text-violet-600">Amount to Pay</p>
                    <p className="text-3xl font-bold text-violet-700">₹{selectedPlan?.price_inr}</p>
                    <p className="text-sm text-violet-500">{selectedPlan?.name}</p>
                  </div>
                  
                  {qrCode ? (
                    <img src={qrCode} alt="Payment QR" className="w-56 h-56 object-contain mx-auto border-2 border-slate-200 rounded-xl p-2" />
                  ) : (
                    <div className="w-56 h-56 bg-slate-100 rounded-xl flex items-center justify-center mx-auto">
                      <p className="text-slate-400">QR not available</p>
                    </div>
                  )}
                  
                  <p className="text-sm text-slate-500">Scan with any UPI app</p>
                  
                  <button
                    onClick={() => setStep('form')}
                    className="w-full py-3 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700"
                  >
                    I've Made the Payment
                  </button>
                  <button onClick={() => setStep('plans')} className="w-full py-2 text-slate-600">← Back</button>
                </div>
              )}
              
              {/* Step 3: Payment Form */}
              {step === 'form' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Amount Paid (₹) *</label>
                    <input
                      type="number"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      placeholder={selectedPlan?.price_inr?.toString()}
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">UTR / Transaction Number *</label>
                    <input
                      type="text"
                      value={formData.utr_number}
                      onChange={(e) => setFormData({ ...formData, utr_number: e.target.value })}
                      placeholder="12-digit transaction reference"
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Screenshot (Optional)</label>
                    <label className="cursor-pointer">
                      <input type="file" accept="image/*" onChange={handleFileUpload} className="hidden" />
                      <div className={`border-2 border-dashed rounded-xl p-4 text-center ${formData.attachment ? 'border-green-300 bg-green-50' : 'border-slate-200'}`}>
                        {formData.attachment ? (
                          <span className="text-green-600 flex items-center justify-center gap-2"><Check size={18} /> Attached</span>
                        ) : (
                          <span className="text-slate-500 flex items-center justify-center gap-2"><Upload size={18} /> Tap to upload</span>
                        )}
                      </div>
                    </label>
                  </div>
                  
                  <button
                    onClick={handleSubmitPayment}
                    disabled={submitting || !formData.amount || !formData.utr_number}
                    className="w-full py-3 bg-violet-600 text-white rounded-xl font-medium hover:bg-violet-700 disabled:opacity-50"
                  >
                    {submitting ? 'Submitting...' : 'Submit for Approval'}
                  </button>
                  <button onClick={() => setStep('qr')} className="w-full py-2 text-slate-600">← Back</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionSettings;
